/**
 * Auth state as the rest of the app reads it.
 *
 * MSAL already holds the authoritative account and token cache; this slice is the
 * read-model over it, so a component that only needs "who is signed in" does not have to
 * take an MSAL dependency. `AuthProvider` is the only writer — it mirrors MSAL's state in
 * here on every change.
 *
 * Nothing is persisted. The MSAL cache is session-scoped (see config/authConfig.ts) and
 * the store deliberately carries no persistence, so a mirrored copy must not outlive it.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface UserProfile {
  displayName: string;
  givenName: string;
  surname: string;
  mail: string;
  userPrincipalName: string;
}

/**
 * The account as the store carries it — deliberately not MSAL's `AccountInfo`.
 *
 * `AccountInfo` carries `idTokenClaims`, and can carry the raw `idToken`. Mirroring it
 * whole put the ID token in Redux, and so in the DevTools timeline of any non-production
 * build, duplicating what MSAL already holds in its own session-scoped cache. These four
 * fields are what identifies the signed-in user; the sensitive artifacts stay in MSAL.
 *
 * Widen this only for a field the app actually renders, and never back to `AccountInfo`.
 */
export interface AuthAccount {
  homeAccountId: string;
  username: string;
  tenantId: string;
  /** The account's display name, or `''` — MSAL leaves it unset for a sparse token. */
  name: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  /** True while MSAL has an interaction (sign-in, redirect hand-back, token) in flight. */
  isLoading: boolean;
  account: AuthAccount | null;
  userProfile: UserProfile | null;
  /**
   * Set when a signed-in account is not from the TESTTRS tenant. The tenanted authority
   * should make this unreachable; it is the client-side backstop that keeps such an
   * account out of the app instead of rendering it as a normal sign-in.
   */
  tenantBlocked: boolean;
  /**
   * The last sign-in failure, as a sentence for the user, or `null`. Set when the redirect
   * hand-back or `loginRedirect` rejects — a cancelled sign-in, a declined consent prompt,
   * a Conditional Access block. Without it those failures return the user to an unchanged
   * sign-in page with no indication that anything went wrong.
   */
  authError: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  account: null,
  userProfile: null,
  tenantBlocked: false,
  authError: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAccount: (state, action: PayloadAction<AuthAccount | null>) => {
      state.account = action.payload;
      state.isAuthenticated = action.payload !== null;
      if (action.payload === null) {
        // `tenantBlocked` is deliberately left alone: a rejected account is reported as
        // no account, so clearing the flag here would erase the very state that says why.
        // `AuthProvider` dispatches `setTenantBlocked` on every change, so sign-out still
        // clears it. `authError` is left alone for the same reason — a failed sign-in
        // leaves no account, and that is exactly when the message has to survive.
        state.userProfile = null;
      } else {
        // An account supersedes any earlier failure: whatever went wrong, it worked now.
        state.authError = null;
      }
    },
    setUserProfile: (state, action: PayloadAction<UserProfile | null>) => {
      state.userProfile = action.payload;
    },
    setTenantBlocked: (state, action: PayloadAction<boolean>) => {
      state.tenantBlocked = action.payload;
    },
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.authError = action.payload;
    },
  },
});

export const { setLoading, setAccount, setUserProfile, setTenantBlocked, setAuthError } =
  authSlice.actions;

export default authSlice.reducer;
